import { useEffect, useMemo, useState, useRef } from 'react';
import _ from 'lodash';
import { FieldErrors } from 'react-hook-form';
import {
  Autocomplete,
  Box,
  FormHelperText,
  MenuItem,
  TextField,
  useTheme,
} from '@mui/material';
import { IconSparkles } from '@tabler/icons-react';

type PackageAutocompleteProps = {
  handleSetValue: (option: string | null) => void;
  errors: FieldErrors;
  name: string;
  label: string;
  options: string[];
  initialValue?: string;
  inline?: boolean;
  mb?: number;
  showAiIcon?: boolean;
};

const PackageAutocomplete = ({
  handleSetValue,
  errors,
  name,
  label,
  options,
  initialValue,
  inline = false,
  mb = 2,
  showAiIcon = false,
}: PackageAutocompleteProps) => {
  const theme = useTheme();
  const [debouncedInput, setDebouncedInput] = useState('');
  const [value, setValue] = useState<string | null>(initialValue ?? '');
  const timeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    setValue(initialValue ?? '');
  }, [initialValue]);

  const optionsWithUserInput = useMemo(
    () => _.uniq(_.compact([...options, debouncedInput])),
    [options, debouncedInput]
  );

  const handleDebounce = (nextValue: string) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setDebouncedInput(nextValue);
    }, 1000);
  };

  return (
    <Autocomplete
      options={optionsWithUserInput}
      onInputChange={(_e, option) => {
        handleDebounce(option);
      }}
      onChange={(__, option) => {
        setValue(option);
        handleSetValue(option);
      }}
      sx={
        inline
          ? {
              '.MuiOutlinedInput-root': { height: '42px' },
              '.MuiAutocomplete-input': {
                minWidth: '50px !important',
              },
            }
          : undefined
      }
      autoComplete
      autoSelect
      filterSelectedOptions
      value={value}
      getOptionLabel={(option) => option}
      size={inline ? 'small' : undefined}
      renderOption={(props, option, { selected }) => (
        <li {...props} key={Math.random()}>
          <MenuItem
            selected={selected}
            sx={{ fontStyle: option === debouncedInput ? 'italic' : 'normal' }}
          >
            {option}
          </MenuItem>
        </li>
      )}
      renderInput={(params) => (
        <Box sx={inline ? { width: '100%' } : { mb }}>
          <TextField
            {...params}
            label={label}
            fullWidth
            sx={
              inline
                ? {
                    '.MuiOutlinedInput-notchedOutline': {
                      borderBottomRightRadius: '0px',
                      borderTopRightRadius: '0px',
                    },
                  }
                : undefined
            }
            slotProps={{
              input: {
                ...params.InputProps,
                startAdornment: showAiIcon ? (
                  <IconSparkles color={theme.palette.success.main} size={20} />
                ) : undefined,
              },
            }}
          />
          {errors?.[name]?.message && (
            <FormHelperText error id={`${name}Error`}>
              {errors?.[name]?.message}
            </FormHelperText>
          )}
        </Box>
      )}
    />
  );
};

export default PackageAutocomplete;
